import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeamModel} from '../../../../../../../libs/shared/core/src/models/api/teamModel';
import {isTeammateNamespace} from '../../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {TeamAvatar} from '../../../../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {
  DropdownItem,
  DropdownItemProps,
} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemInboxDot} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemInboxDot';
import {DropdownItemSecondaryContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemSecondaryContent';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipOverflowCondition} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisuallyHidden} from '../../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

interface MoveDropdownItemProps extends DropdownItemProps {
  inbox: InboxModel;
  team?: TeamModel;
  shouldShowDeduplication: boolean;
  shouldShowTeammateCount: boolean;
  isBold: boolean;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  align-items: center;
  display: grid;
  grid-column-gap: 5px;
  grid-auto-flow: column;
`;

const StyledInboxDiv = styled.div`
  ${ellipsis()}
`;

/*
 * Component.
 */

export const MoveDropdownItem: FC<MoveDropdownItemProps> = (props) => (
  <DropdownItem {...props} renderTooltip={() => props.inbox.name}>
    <DropdownItemInboxDot inbox={props.inbox} />
    <DropdownItemContent isChecked={props.isChecked} isBold={props.isBold}>
      <StyledContentDiv>
        {renderInboxName(props)}
        {maybeRenderTeamIcon(props)}
      </StyledContentDiv>
    </DropdownItemContent>
    {maybeRenderTeammateCount(props)}
  </DropdownItem>
);

function renderInboxName(props: MoveDropdownItemProps) {
  return (
    <StyledInboxDiv>
      <TooltipOverflowCondition>{props.inbox.name}</TooltipOverflowCondition>
    </StyledInboxDiv>
  );
}

function maybeRenderTeamIcon(props: MoveDropdownItemProps) {
  const {inbox, shouldShowDeduplication, team} = props;

  // If this is a private inbox, show the lock icon.
  const isTeammateInbox = isTeammateNamespace(inbox.namespace);
  if (isTeammateInbox) {
    return (
      <>
        <VisuallyHidden>
          <Trans id="moveitem-private">Private</Trans>
        </VisuallyHidden>
        <UserThemeConsumer>
          {(theme) => <Icon name="lockSmall" color={theme.greys.shade60} />}
        </UserThemeConsumer>
      </>
    );
  }

  // Otherwise, if deduplication was requested, render the team avatar.
  if (shouldShowDeduplication && team) {
    return (
      <>
        <VisuallyHidden>{team.name}</VisuallyHidden>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
      </>
    );
  }

  return null;
}

function maybeRenderTeammateCount(props: MoveDropdownItemProps) {
  const {inbox, team} = props;

  if (!props.shouldShowTeammateCount) {
    return null;
  }

  // Public inboxes grant access to the entire team.
  if (inbox.isPublic && team) {
    return <DropdownItemSecondaryContent>{team.name}</DropdownItemSecondaryContent>;
  }

  return (
    <DropdownItemSecondaryContent>
      <Plural
        id="move-dropdown-members"
        value={inbox.individualTeammateIds.length}
        one="# member"
        other="# members"
      />
    </DropdownItemSecondaryContent>
  );
}
